import React, { useState, useEffect } from 'react';
import { Clock, Calendar, Edit2, Trash2, Printer, Download, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ModuleInfo } from './ui/module-info';
import { usePersistedState } from '../hooks/usePersistedState';
import axios from 'axios';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

// Normaliza chave de agrupamento para "YYYY-MM-DD" mesmo quando o backend
// devolver datetime ISO completo nos dados legados (ex.: "2026-04-15T00:00:00.000000").
// Função pura — declarada fora do componente para evitar TDZ entre helpers internos.
const _diaKey = (d) => (d || "").slice(0, 10);

const RegistoPontoTab = () => {
  const [registos, setRegistos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mesFiltro, setMesFiltro] = usePersistedState('regponto_mes', new Date().getMonth() + 1);
  const [anoFiltro, setAnoFiltro] = usePersistedState('regponto_ano', new Date().getFullYear());
  const [showModal, setShowModal] = useState(false);
  const [showRegistoModal, setShowRegistoModal] = useState(false);
  const [tipoRegistoModal, setTipoRegistoModal] = useState('entrada');
  const [editingRegisto, setEditingRegisto] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canFilterColaboradores, setCanFilterColaboradores] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [colaboradores, setColaboradores] = useState([]);
  const [colaboradorFiltro, setColaboradorFiltro] = usePersistedState('regponto_colab', '');
  const [horariosConfig, setHorariosConfig] = useState([]);
  const [lojaSelecionada, setLojaSelecionada] = useState('');
  const [turnoSelecionado, setTurnoSelecionado] = useState('');
  const [intervaloSelecionado, setIntervaloSelecionado] = useState(null);
  const [showHorariosConfig, setShowHorariosConfig] = useState(false);
  const [editingHorarioConfig, setEditingHorarioConfig] = useState(null);
  const [editHorarioData, setEditHorarioData] = useState(null);
  const [turnosDoDia, setTurnosDoDia] = useState([]); // Todos os turnos do dia
  const [temEntradaAbertaHoje, setTemEntradaAbertaHoje] = useState(false);
  // 🆕 iter287 — Horário mensal por dia para impressão (folga/férias/troca/etc.)
  const [horarioMesPorDia, setHorarioMesPorDia] = useState({});
  
  // Helper para selecionar todo o texto quando foca num input numérico
  const handleFocusSelect = (e) => {
    e.target.select();
    // Se o valor é 0, limpar o campo para facilitar a escrita
    if (e.target.value === '0' || e.target.value === '0.00') {
      setTimeout(() => {
        e.target.value = '';
        e.target.dispatchEvent(new Event('input', { bubbles: true }));
      }, 0);
    }
  };
  
  const [formData, setFormData] = useState({
    data: new Date().toISOString().split('T')[0],
    tipo: 'entrada',
    hora: new Date().toTimeString().slice(0, 5),
    observacoes: ''
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsAdmin(user.role === 'admin');
    setCanFilterColaboradores(user.role === 'admin');
    setCurrentUser(user);
    if (user.role === 'admin') {
      loadColaboradores();
    }
    loadRegistos();
    loadHorariosConfig();
    loadHorarioMes();
  }, [mesFiltro, anoFiltro, colaboradorFiltro]);

  const checkUserRole = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsAdmin(user.role === 'admin');
    setCanFilterColaboradores(user.role === 'admin');
    setCurrentUser(user);
  };

  const loadColaboradores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setColaboradores(response.data);
    } catch (err) {
      console.error('Erro ao carregar colaboradores', err);
    }
  };

  // Detectar horário atual do colaborador automaticamente
  const detectarHorarioAtual = () => {
    if (!currentUser || !horariosConfig.length) return null;
    
    const agora = new Date();
    const horaAtual = agora.toTimeString().slice(0, 5); // "HH:MM"
    const diaAtual = agora.getDay(); // 0-6 (domingo a sábado)
    const tipoDia = (diaAtual === 0 || isFeriado(agora)) ? 'feriado_domingo' : 'normal';
    
    // Buscar horários do colaborador para hoje
    let melhorMatch = null;
    let menorDiferenca = Infinity;
    
    horariosConfig.forEach(config => {
      if (config.tipo_dia !== tipoDia) return;
      
      // Verificar cada turno
      Object.keys(config.horarios || {}).forEach(turno => {
        const intervalos = config.horarios[turno] || [];
        
        intervalos.forEach(intervalo => {
          const horaInicio = intervalo.inicio;
          const horaFim = intervalo.fim;
          
          // Calcular diferença em minutos
          const [horaH, horaM] = horaAtual.split(':').map(Number);
          const [inicioH, inicioM] = horaInicio.split(':').map(Number);
          const [fimH, fimM] = horaFim.split(':').map(Number);
          
          const minutosAtual = horaH * 60 + horaM;
          const minutosInicio = inicioH * 60 + inicioM;
          const minutosFim = fimH * 60 + fimM;
          
          // Se está perto da hora de início (até 30 min antes ou depois)
          const diferencaInicio = Math.abs(minutosAtual - minutosInicio);
          if (diferencaInicio <= 30 && diferencaInicio < menorDiferenca) {
            menorDiferenca = diferencaInicio;
            melhorMatch = {
              loja: config.loja,
              turno: turno,
              tipo: 'entrada',
              hora: horaInicio,
              intervalo: intervalo
            };
          }
          
          // Se está perto da hora de saída (até 30 min antes ou depois)
          const diferencaSaida = Math.abs(minutosAtual - minutosFim);
          if (diferencaSaida <= 30 && diferencaSaida < menorDiferenca) {
            menorDiferenca = diferencaSaida;
            melhorMatch = {
              loja: config.loja,
              turno: turno,
              tipo: 'saida',
              hora: horaFim,
              intervalo: intervalo
            };
          }
        });
      });
    });
    
    return melhorMatch;
  };
  
  const loadHorariosConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/horarios-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHorariosConfig(response.data);
    } catch (err) {
      console.error('Erro ao carregar configurações de horários', err);
    }
  };

  const loadRegistos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = { mes: mesFiltro, ano: anoFiltro };
      if (colaboradorFiltro) params.colaborador_id = colaboradorFiltro;
      
      const response = await axios.get(`${API_URL}/api/registos-ponto`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistos(response.data);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar registos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 🆕 iter287 — Carregar horário mensal do colaborador (para o print marcar
  // folga/férias/baixa/falta/feriado/reunião/troca em vez de "---").
  const loadHorarioMes = async () => {
    try {
      const token = localStorage.getItem('token');
      // colaborador_id: filtro (se admin escolheu) ou próprio user
      const colabId = colaboradorFiltro || currentUser?.id;
      if (!colabId) { setHorarioMesPorDia({}); return; }
      const response = await axios.get(
        `${API_URL}/api/horarios-mensais-admin/horario-mes-colaborador/${colabId}`,
        {
          params: { ano: anoFiltro, mes: mesFiltro },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setHorarioMesPorDia(response.data?.por_dia || {});
    } catch (err) {
      // 403 acontece se colaborador tenta ver outro colab — silenciar.
      // 404 quando não há horário para o mês — normal.
      setHorarioMesPorDia({});
    }
  };

  // Estado real de "entrada aberta" do CURRENT USER para HOJE
  // Independente dos filtros mês/ano/colaborador do ecrã (admin/responsável podem
  // estar a ver outros colaboradores) — garante que os botões ENTRADA/SAÍDA
  // reflectem sempre o estado pessoal do utilizador autenticado.
  const recalcularEntradaAberta = async () => {
    try {
      const token = localStorage.getItem('token');
      const hoje = new Date().toISOString().split('T')[0];
      const resp = await axios.get(`${API_URL}/api/registos-ponto`, {
        params: { data: hoje },
        headers: { Authorization: `Bearer ${token}` }
      });
      const meus = (resp.data || []).filter(r => {
        // O backend já filtra por current_user para não-admin; admins recebem
        // todos os registos do dia, então temos de filtrar manualmente.
        if (!currentUser?.id) return true;
        return r.colaborador_id === currentUser.id;
      });
      const entradas = meus.filter(r => r.tipo === 'entrada').length;
      const saidas = meus.filter(r => r.tipo === 'saida').length;
      setTemEntradaAbertaHoje(entradas > saidas);
    } catch (err) {
      console.error('Erro ao calcular entrada aberta', err);
    }
  };

  useEffect(() => {
    if (currentUser?.id) recalcularEntradaAberta();
    if (currentUser?.id) loadHorarioMes();
  }, [currentUser?.id]);

  // Buscar turno agendado no horário mensal
  const buscarTurnoAgendado = async () => {
    if (!currentUser || !currentUser.id) return null;
    
    try {
      const hoje = new Date().toISOString().split('T')[0];
      const token = localStorage.getItem('token');
      
      // Buscar turnos agendados
      const response = await axios.get(
        `${API_URL}/api/turno-agendado/${currentUser.id}/${hoje}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const turnos = response.data.turnos_agendados || [];
      
      // Ordenar turnos pela hora de entrada (mais cedo primeiro)
      turnos.sort((a, b) => {
        const horaA = a.entrada || '00:00';
        const horaB = b.entrada || '00:00';
        return horaA.localeCompare(horaB);
      });
      
      // Guardar todos os turnos do dia para mostrar no resumo
      setTurnosDoDia(turnos);
      
      if (turnos.length === 0) {
        return null;
      }
      
      // Buscar registos de ponto de HOJE para verificar quais turnos já foram completados
      const registosResponse = await axios.get(
        `${API_URL}/api/registos-ponto`,
        { 
          params: { 
            mes: new Date().getMonth() + 1, 
            ano: new Date().getFullYear(),
            colaborador_id: currentUser.id
          },
          headers: { Authorization: `Bearer ${token}` } 
        }
      );
      
      const registosHoje = (registosResponse.data || []).filter(r => _diaKey(r.data) === hoje);
      
      // Agrupar registos por turno (identificado pela hora de entrada)
      const turnosCompletados = [];
      
      // Verificar cada turno agendado (já ordenado por hora de entrada)
      for (let i = 0; i < turnos.length; i++) {
        const turno = turnos[i];
        
        // Verificar se este turno já tem entrada registada
        const entradaRegistada = registosHoje.find(r => 
          r.tipo === 'entrada' && 
          r.hora === turno.entrada
        );
        
        // Verificar se este turno já tem saída registada  
        const saidaRegistada = registosHoje.find(r => 
          r.tipo === 'saida' && 
          r.hora === turno.saida
        );
        
        // Se tem entrada E saída, o turno está completo
        if (entradaRegistada && saidaRegistada) {
          turnosCompletados.push(i);
        } else if (entradaRegistada && !saidaRegistada) {
          // Turno em progresso - tem entrada mas não saída
          // Retornar este turno para registar a saída
          return {
            local: turno.local,
            entrada: turno.entrada,
            saida: turno.saida,
            tipo: turno.tipo || 'normal',
            turnoIndex: i
          };
        } else {
          // Turno ainda não iniciado - retornar para registar entrada
          return {
            local: turno.local,
            entrada: turno.entrada,
            saida: turno.saida,
            tipo: turno.tipo || 'normal',
            turnoIndex: i
          };
        }
      }
      
      // Se chegou aqui, todos os turnos estão completos
      return null;
      
    } catch (err) {
      console.error('Erro ao buscar turno agendado:', err);
      return null;
    }
  };

  const handleRegistarPonto = async (tipo) => {
    // PRIORIDADE 1: Buscar turno agendado no horário mensal
    const turnoAgendado = await buscarTurnoAgendado();
    
    if (turnoAgendado) {
      // Usar turno do horário mensal
      setTipoRegistoModal(tipo);
      setLojaSelecionada(turnoAgendado.local);
      
      // Para o turno, vamos usar o local como identificador único
      // já que o sistema antigo usava turnos (manhã/tarde)
      setTurnoSelecionado('turno_agendado');
      
      // Definir a hora baseada no tipo de registo
      const hora = tipo === 'entrada' ? turnoAgendado.entrada : turnoAgendado.saida;
      setIntervaloSelecionado({ 
        hora: hora, 
        tipo: tipo === 'entrada' ? 'inicio' : 'fim',
        from_horario_mensal: true 
      });
      
    } else {
      // PRIORIDADE 2 (Fallback): Usar detecção automática do sistema antigo
      const horarioDetectado = detectarHorarioAtual();
      
      if (horarioDetectado) {
        // Pré-selecionar automaticamente - USAR O TIPO DO BOTÃO CLICADO
        setTipoRegistoModal(tipo);  // FIX: usar o parâmetro 'tipo' do botão clicado
        setLojaSelecionada(horarioDetectado.loja);
        setTurnoSelecionado(horarioDetectado.turno);
        setIntervaloSelecionado({ hora: horarioDetectado.hora, tipo: tipo === 'entrada' ? 'inicio' : 'fim' });
      } else {
        // Sem horário detectado, deixar usuário escolher manualmente
        setTipoRegistoModal(tipo);
        setLojaSelecionada('');
        setTurnoSelecionado('');
        setIntervaloSelecionado(null);
      }
    }
    
    setShowRegistoModal(true);
  };

  const confirmarRegistoPonto = async () => {
    if (!lojaSelecionada || !intervaloSelecionado) {
      setError('Por favor, verifique os dados do turno');
      return;
    }
    
    // Se não há turno selecionado, usar um valor padrão para turnos agendados
    const turnoFinal = turnoSelecionado || 'turno_agendado';

    const now = new Date();
    // Hora local do browser (assume Portugal — Lisboa). O backend
    // re-deriva hora_real em Europe/Lisbon, mas enviamos local mesmo assim.
    const pad = (n) => String(n).padStart(2, '0');
    const data = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const hora = intervaloSelecionado.hora; // Hora selecionada/agendada pelo colaborador
    const hora_real = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/registos-ponto`,
        { 
          data, 
          tipo: tipoRegistoModal, 
          hora, 
          hora_real,
          loja: lojaSelecionada,
          turno: turnoFinal,
          observacoes: '' 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowRegistoModal(false);
      setLojaSelecionada('');
      setTurnoSelecionado('');
      setIntervaloSelecionado(null);
      setError(null);
      loadRegistos();
      recalcularEntradaAberta();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao registar ponto');
    }
  };

  const getIntervalosDisponiveis = () => {
    if (!lojaSelecionada || !turnoSelecionado) return [];
    
    const hoje = new Date();
    const tipoDia = (hoje.getDay() === 0 || isFeriado(hoje)) ? 'feriado_domingo' : 'normal';
    
    const config = horariosConfig.find(c => c.loja === lojaSelecionada && c.tipo_dia === tipoDia);
    if (!config) return [];
    
    const intervalos = config.horarios[turnoSelecionado] || [];
    
    // Para entrada, mostrar apenas horas de início (únicas)
    if (tipoRegistoModal === 'entrada') {
      const horasInicio = [...new Set(intervalos.map(i => i.inicio))];
      return horasInicio.map(hora => ({ hora, tipo: 'inicio' }));
    }
    
    // Para saída, mostrar apenas horas de fim (únicas)
    if (tipoRegistoModal === 'saida') {
      const horasFim = [...new Set(intervalos.map(i => i.fim))];
      return horasFim.map(hora => ({ hora, tipo: 'fim' }));
    }
    
    return [];
  };

  const isFeriado = (date) => {
    // Simplified check - in production, use proper holiday API
    return false;
  };

  const handleEditHorario = (config) => {
    setEditingHorarioConfig(config.id);
    setEditHorarioData(JSON.parse(JSON.stringify(config.horarios)));
  };

  const handleSaveHorario = async (configId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/horarios-config/${configId}`,
        { horarios: editHorarioData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingHorarioConfig(null);
      setEditHorarioData(null);
      loadHorariosConfig();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar configuração');
    }
  };

  const handleCancelEditHorario = () => {
    setEditingHorarioConfig(null);
    setEditHorarioData(null);
  };

  const updateIntervaloHorario = (turno, index, field, value) => {
    const newEditData = { ...editHorarioData };
    newEditData[turno][index][field] = value;
    setEditHorarioData(newEditData);
  };

  const addIntervaloHorario = (turno) => {
    const newEditData = { ...editHorarioData };
    newEditData[turno].push({ inicio: '09:00', fim: '17:00' });
    setEditHorarioData(newEditData);
  };

  const removeIntervaloHorario = (turno, index) => {
    const newEditData = { ...editHorarioData };
    newEditData[turno].splice(index, 1);
    setEditHorarioData(newEditData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingRegisto) {
        await axios.put(
          `${API_URL}/api/registos-ponto/${editingRegisto.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/registos-ponto`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setShowModal(false);
      resetForm();
      loadRegistos();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar registo');
    }
  };

  const handleEdit = (registo) => {
    setEditingRegisto(registo);
    setFormData({
      data: _diaKey(registo.data),  // normaliza para YYYY-MM-DD (input type=date)
      tipo: registo.tipo,
      hora: registo.hora,
      observacoes: registo.observacoes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (registoId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este registo?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/registos-ponto/${registoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadRegistos();
    } catch (err) {
      setError('Erro ao eliminar registo');
    }
  };

  const resetForm = () => {
    setEditingRegisto(null);
    setFormData({
      data: new Date().toISOString().split('T')[0],
      tipo: 'entrada',
      hora: new Date().toTimeString().slice(0, 5),
      observacoes: ''
    });
  };

  const updateField = (field, value) => {
    setFormData({...formData, [field]: value});
  };

  const calcularTotalHoras = () => {
    const registosPorDia = {};
    
    registos.forEach(reg => {
      const k = _diaKey(reg.data);
      if (!k) return;
      if (!registosPorDia[k]) {
        registosPorDia[k] = { entradas: [], saidas: [] };
      }
      
      // Extrair apenas a hora, ignorando intervalos (ex: "07:00-13:30" -> "07:00")
      let hora = reg.hora;
      if (hora && hora.includes('-')) {
        hora = hora.split('-')[0].trim();
      }
      
      if (reg.tipo === 'entrada') {
        registosPorDia[k].entradas.push(hora);
      } else {
        registosPorDia[k].saidas.push(hora);
      }
    });
    
    let totalMinutos = 0;
    
    Object.values(registosPorDia).forEach(dia => {
      dia.entradas.sort();
      dia.saidas.sort();
      
      const pares = Math.min(dia.entradas.length, dia.saidas.length);
      
      for (let i = 0; i < pares; i++) {
        const entradaParts = dia.entradas[i].split(':');
        const saidaParts = dia.saidas[i].split(':');
        
        if (entradaParts.length >= 2 && saidaParts.length >= 2) {
          const [hE, mE] = entradaParts.map(Number);
          const [hS, mS] = saidaParts.map(Number);
          
          const minutosEntrada = hE * 60 + mE;
          const minutosSaida = hS * 60 + mS;
          
          totalMinutos += (minutosSaida - minutosEntrada);
        }
      }
    });
    
    const horas = Math.floor(totalMinutos / 60);
    const minutos = totalMinutos % 60;
    
    return `${horas}h ${minutos}m`;
  };

  const handleImprimir = () => {
    window.print();
  };

  // Normaliza chave de agrupamento para "YYYY-MM-DD" mesmo quando o backend
  // devolver datetime ISO completo nos dados legados (ex.: "2026-04-15T00:00:00.000000").
  // Isto evita "Invalid Date" no header dos cards e garante agrupamento correcto.
  const registosPorDia = registos.reduce((acc, reg) => {
    const k = _diaKey(reg.data);
    if (!k) return acc;
    if (!acc[k]) {
      acc[k] = [];
    }
    acc[k].push(reg);
    return acc;
  }, {});

  // Função para gerar todos os dias do mês (para impressão)
  const getAllDaysInMonth = () => {
    const year = anoFiltro;
    const month = mesFiltro - 1; // JavaScript months are 0-indexed
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const allDays = {};
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split('T')[0];
      allDays[dateStr] = registosPorDia[dateStr] || [];
    }
    
    return allDays;
  };

  if (loading) return <div className="text-center py-8">Carregando...</div>;

  return (
    <div className="space-y-6">
      {/* ModuleInfo - Ajuda contextual */}
      <div className="flex items-center gap-2">
        <ModuleInfo
          descricao="Registo diario de entradas e saidas dos colaboradores. Permite marcar ponto rapidamente (Entrada/Saida), consultar o resumo mensal com horas trabalhadas, atrasos e horas extra, e configurar horarios-padrao por loja e turno."
          dados="Registos de ponto guardados em /api/registos-ponto. Horarios configurados em /api/horarios-config. Lista de colaboradores de /api/users."
          calculo="Atraso = diferenca entre hora de entrada e inicio do turno agendado. Horas extra = tempo alem do fim do turno. O sistema detecta automaticamente o turno agendado para o dia."
          legendas="Verde = dentro do horario. Amarelo = atraso ligeiro. Vermelho = atraso significativo. Botao Entrada/Saida regista o momento atual."
        />
      </div>
      {/* Registo Rápido - Para Admin e Colaboradores */}
      <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white no-print shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold mb-2">Registo Rapido de Ponto</h2>
              <p className="text-blue-100">
                {currentUser?.nome || 'Colaborador'} - {new Date().toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <div className="flex gap-4">
              <Button 
                onClick={() => handleRegistarPonto('entrada')} 
                size="lg"
                disabled={temEntradaAbertaHoje}
                data-testid="btn-registar-entrada"
                className={`font-bold py-6 px-8 text-lg shadow-xl ${temEntradaAbertaHoje ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                title={temEntradaAbertaHoje ? 'Já existe uma entrada aberta. Registe a saída antes de nova entrada.' : 'Registar entrada'}
              >
                <Clock className="h-6 w-6 mr-2" />
                ENTRADA
              </Button>
              <Button 
                onClick={() => handleRegistarPonto('saida')} 
                size="lg"
                disabled={!temEntradaAbertaHoje}
                data-testid="btn-registar-saida"
                className={`font-bold py-6 px-8 text-lg shadow-xl ${temEntradaAbertaHoje ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-gray-400 text-gray-200 cursor-not-allowed'}`}
                title={!temEntradaAbertaHoje ? 'Não existe entrada aberta para registar saída.' : 'Registar saída'}
              >
                <Clock className="h-6 w-6 mr-2" />
                SAÍDA
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          
          /* Configuração da página */
          @page {
            size: A4 portrait;
            margin: 0.5cm 1cm;
          }
          
          html, body {
            height: 100%;
            margin: 0;
            padding: 0;
            font-size: 9pt;
            line-height: 1.2;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          /* Container principal */
          .print-container {
            height: 100vh;
            display: flex;
            flex-direction: column;
          }
          
          /* Cabeçalho compacto */
          .print-header {
            padding-bottom: 0.3cm;
            margin-bottom: 0.3cm;
            border-bottom: 2px solid #000;
          }
          
          .print-header h1 {
            font-size: 16pt !important;
            margin: 0 0 0.2cm 0 !important;
            font-weight: bold;
          }
          
          .print-header p {
            font-size: 10pt !important;
            margin: 0.1cm 0 !important;
          }
          
          /* Lista de dias compacta */
          .print-days {
            flex: 1;
            overflow: hidden;
          }
          
          .print-day-row {
            display: flex;
            align-items: center;
            padding: 0.05cm 0;
            border-bottom: 1px solid #ddd;
            font-size: 8pt;
            line-height: 1.1;
          }
          
          .print-day-row:last-child {
            border-bottom: none;
          }
          
          /* Assinaturas compactas */
          .print-signatures {
            margin-top: 0.5cm;
            padding-top: 0.3cm;
            border-top: 2px solid #000;
          }
          
          .print-signatures p {
            font-size: 8pt !important;
            margin: 0.2cm 0 !important;
          }
          
          /* Garantir que cards não quebrem entre páginas */
          .print-card {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* Cores em preto para impressão */
          h1, h2, h3, h4 {
            color: black !important;
          }
        }
        .print-only {
          display: none;
        }
      `}</style>

      {/* Header */}
      <Card className="bg-gradient-card border-primary/20 no-print">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4 flex-wrap">
              <Calendar className="h-5 w-5 text-primary" />
              <select
                value={mesFiltro}
                onChange={(e) => setMesFiltro(Number(e.target.value))}
                className="px-3 py-2 border rounded-md bg-background"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2000, i, 1).toLocaleDateString('pt-PT', { month: 'long' })}
                  </option>
                ))}
              </select>
              
              <select
                value={anoFiltro}
                onChange={(e) => setAnoFiltro(Number(e.target.value))}
                className="px-3 py-2 border rounded-md bg-background"
              >
                {Array.from({ length: 5 }, (_, i) => (
                  <option key={i} value={new Date().getFullYear() - 2 + i}>
                    {new Date().getFullYear() - 2 + i}
                  </option>
                ))}
              </select>

              {canFilterColaboradores && (
                <select
                  value={colaboradorFiltro}
                  onChange={(e) => setColaboradorFiltro(e.target.value)}
                  className="px-3 py-2 border rounded-md bg-background"
                >
                  <option value="">Todos os colaboradores</option>
                  {colaboradores.map(colab => (
                    <option key={colab.id} value={colab.id}>{colab.nome}</option>
                  ))}
                </select>
              )}
            </div>
            
            <Button onClick={handleImprimir} variant="outline">
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded no-print">
          {error}
        </div>
      )}

      {/* Cabeçalho de Impressão */}
      <div className="print-only print-header">
        <div className="text-center">
          <h1>REGISTO DE PONTO</h1>
          <p className="font-semibold uppercase">
            {new Date(anoFiltro, mesFiltro - 1, 1).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div style={{ marginTop: '0.3cm' }}>
          <p className="font-semibold">
            Colaborador: {colaboradorFiltro 
              ? colaboradores.find(c => c.id === colaboradorFiltro)?.nome 
              : currentUser?.nome || 'Todos os Colaboradores'}
          </p>
        </div>
        
        {/* Cabeçalhos das colunas */}
        <div style={{ display: 'flex', marginTop: '0.4cm', paddingBottom: '0.1cm', borderBottom: '1px solid #000', fontWeight: 'bold', fontSize: '9pt' }}>
          <div style={{ width: '70px' }}>Data</div>
          <div style={{ width: '100px', marginLeft: '5px' }}>Seção</div>
          <div style={{ flex: 1, marginLeft: '10px' }}>Registos</div>
          <div style={{ width: '70px', textAlign: 'right' }}>Total H.</div>
        </div>
      </div>

      {/* Resumo */}
      <Card className="bg-gradient-card no-print">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Resumo do Mês
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total de Registos</p>
              <p className="text-2xl font-bold text-primary">{registos.length}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Dias Trabalhados</p>
              <p className="text-2xl font-bold text-success">{Object.keys(registosPorDia).length}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total de Horas</p>
              <p className="text-2xl font-bold text-accent">{calcularTotalHoras()}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Média Diária</p>
              <p className="text-2xl font-bold">
                {Object.keys(registosPorDia).length > 0
                  ? `${Math.round(registos.length / Object.keys(registosPorDia).length)} reg/dia`
                  : '0'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Registos por Dia - Versão Tela */}
      <div className="space-y-4 no-print">
        {Object.keys(registosPorDia).length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum registo de ponto neste período</p>
            </CardContent>
          </Card>
        ) : (
          Object.entries(registosPorDia)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([data, regs]) => (
              <Card key={data}>
                <CardHeader>
                  <CardTitle className="text-base">
                    {new Date(data + 'T00:00:00').toLocaleDateString('pt-PT', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {regs
                      .sort((a, b) => a.hora.localeCompare(b.hora))
                      .map((reg) => (
                        <div
                          key={reg.id}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/5"
                        >
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              reg.tipo === 'entrada'
                                ? 'bg-success/20 text-success'
                                : 'bg-warning/20 text-warning'
                            }`}>
                              {reg.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span className="font-bold text-lg">{reg.hora}</span>
                              {isAdmin && reg.hora_real ? (
                                <span className={`text-xs px-2 py-1 rounded ${
                                  reg.hora_real !== reg.hora 
                                    ? 'text-orange-600 bg-orange-100 font-semibold' 
                                    : 'text-muted-foreground bg-accent/30'
                                }`}>
                                  Registo: {reg.hora_real}
                                </span>
                              ) : isAdmin ? (
                                <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                                  Sem hora_real
                                </span>
                              ) : null}
                            </div>
                            {isAdmin && reg.colaborador_nome && (
                              <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-lg">
                                <span className="text-sm font-semibold text-blue-700">
                                  {reg.colaborador_nome}
                                </span>
                              </div>
                            )}
                            {reg.observacoes && (
                              <span className="text-sm text-muted-foreground italic">
                                {reg.observacoes}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2 no-print">
                            <Button size="sm" variant="outline" onClick={() => handleEdit(reg)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDelete(reg.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            ))
        )}
      </div>

      {/* Lista de Registos por Dia - Versão Impressão (Todos os dias) */}
      <div className="print-only print-days">
        {Object.entries(getAllDaysInMonth())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([data, regs]) => {
            // Calcular total de horas do dia
            const calcularHorasDia = () => {
              if (regs.length < 2) return '---';
              
              const entradas = regs.filter(r => r.tipo === 'entrada').sort((a, b) => a.hora.localeCompare(b.hora));
              const saidas = regs.filter(r => r.tipo === 'saida').sort((a, b) => a.hora.localeCompare(b.hora));
              
              let totalMinutos = 0;
              for (let i = 0; i < Math.min(entradas.length, saidas.length); i++) {
                const [hEntrada, mEntrada] = entradas[i].hora.split(':').map(Number);
                const [hSaida, mSaida] = saidas[i].hora.split(':').map(Number);
                const minEntrada = hEntrada * 60 + mEntrada;
                const minSaida = hSaida * 60 + mSaida;
                totalMinutos += minSaida - minEntrada;
              }
              
              const horas = Math.floor(totalMinutos / 60);
              const minutos = totalMinutos % 60;
              return `${horas}h${minutos.toString().padStart(2, '0')}m`;
            };
            
            // 🆕 iter287 — Info do horário do dia (folga/férias/troca/etc.)
            const turnosDia = horarioMesPorDia[data] || [];
            const tipoEspecial = turnosDia.find(t => ['folga','folga_compensacao','ferias','falta','baixa','feriado','reuniao'].includes((t.tipo||'').toLowerCase()));
            const temTroca = turnosDia.some(t => t.e_troca === true);
            const LABELS_TIPO = {
              folga: 'FOLGA', ferias: 'FÉRIAS', falta: 'FALTA',
              baixa: 'BAIXA', feriado: 'FERIADO', reuniao: 'REUNIÃO',
            };

            // Obter seção (loja) do primeiro registo — ou do horário se sem registos
            let secao = '---';
            if (regs.length > 0 && regs[0].loja) {
              secao = regs[0].loja;
            } else if (turnosDia.length > 0) {
              const primeiro = turnosDia[0];
              const tipoT = (primeiro.tipo || '').toLowerCase();
              // Reunião: mostrar sempre "REUNIÃO" (local é irrelevante, é só placeholder)
              if (tipoT === 'reuniao') {
                secao = 'REUNIÃO';
              } else if (primeiro.local) {
                secao = primeiro.local;
              }
            }
            const horasDia = calcularHorasDia();
            
            return (
              <div key={data} className="print-day-row">
                <div style={{ width: '70px', fontWeight: '600', fontSize: '8pt' }}>
                  {new Date(data + 'T00:00:00').toLocaleDateString('pt-PT', {
                    weekday: 'short',
                    day: '2-digit',
                    month: 'short'
                  })}
                </div>
                <div style={{ width: '100px', fontSize: '8pt', marginLeft: '5px' }}>
                  {secao}
                  {temTroca && (
                    <span style={{ marginLeft: '4px', fontSize: '7pt', fontWeight: 'bold',
                                   backgroundColor: '#fff3cd', color: '#856404',
                                   padding: '1px 4px', borderRadius: '2px', border: '1px solid #856404' }}>
                      TROCA
                    </span>
                  )}
                </div>
                <div style={{ flex: 1, marginLeft: '10px' }}>
                  {regs.length > 0 ? (
                    <span style={{ fontSize: '8pt' }}>
                      {regs.map((reg, idx) => (
                        <span key={idx} style={{ marginRight: '10px' }}>
                          {reg.tipo === 'entrada' ? '→' : '←'} {reg.hora}
                        </span>
                      ))}
                    </span>
                  ) : tipoEspecial ? (
                    <span style={{ fontSize: '8pt', fontWeight: 'bold', color: '#555' }}>
                      {LABELS_TIPO[(tipoEspecial.tipo || '').toLowerCase()] || tipoEspecial.tipo.toUpperCase()}
                      {tipoEspecial.observacao && tipoEspecial.observacao !== LABELS_TIPO[(tipoEspecial.tipo || '').toLowerCase()] ?
                        ` — ${tipoEspecial.observacao}` : ''}
                    </span>
                  ) : (
                    <span style={{ color: '#999', fontSize: '8pt' }}>---</span>
                  )}
                </div>
                <div style={{ width: '70px', fontWeight: '600', fontSize: '8pt', textAlign: 'right' }}>
                  {horasDia}
                </div>
              </div>
            );
          })
        }
        
        {/* Linha de Total */}
        <div className="print-day-row" style={{ borderTop: '2px solid #000', marginTop: '0.3cm', paddingTop: '0.2cm', fontWeight: 'bold' }}>
          <div style={{ width: '70px', fontSize: '9pt' }}>TOTAL:</div>
          <div style={{ flex: 1 }}></div>
          <div style={{ width: '70px', fontSize: '9pt', textAlign: 'right' }}>
            {(() => {
              let totalMinutos = 0;
              Object.values(getAllDaysInMonth()).forEach(regs => {
                if (regs.length < 2) return;
                
                const entradas = regs.filter(r => r.tipo === 'entrada').sort((a, b) => a.hora.localeCompare(b.hora));
                const saidas = regs.filter(r => r.tipo === 'saida').sort((a, b) => a.hora.localeCompare(b.hora));
                
                for (let i = 0; i < Math.min(entradas.length, saidas.length); i++) {
                  const [hEntrada, mEntrada] = entradas[i].hora.split(':').map(Number);
                  const [hSaida, mSaida] = saidas[i].hora.split(':').map(Number);
                  const minEntrada = hEntrada * 60 + mEntrada;
                  const minSaida = hSaida * 60 + mSaida;
                  totalMinutos += minSaida - minEntrada;
                }
              });
              
              const horas = Math.floor(totalMinutos / 60);
              const minutos = totalMinutos % 60;
              return `${horas}h${minutos.toString().padStart(2, '0')}m`;
            })()}
          </div>
        </div>
      </div>

      {/* Modal de Registo Rápido */}
      {showRegistoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 no-print">
          <div className="bg-card rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Registar {tipoRegistoModal === 'entrada' ? 'Entrada' : 'Saída'}
              </h2>
              <p className="text-sm text-muted-foreground mt-2">
                Hora: {new Date().toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              {error && (
                <div className="bg-destructive/10 border border-destructive text-destructive px-3 py-2 rounded text-sm">
                  {error}
                </div>
              )}
              
              {lojaSelecionada && intervaloSelecionado && (
                <div className="bg-green-50 border border-green-300 text-green-800 px-4 py-3 rounded-lg text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">✅</span>
                    <div>
                      <p className="font-semibold">
                        {intervaloSelecionado.from_horario_mensal 
                          ? '📅 Turno Agendado Detectado!' 
                          : 'Horário Detectado Automaticamente!'}
                      </p>
                      <p className="text-xs mt-1">
                        {lojaSelecionada} • {intervaloSelecionado.hora}
                      </p>
                      <p className="text-xs mt-1 text-green-600">
                        {intervaloSelecionado.from_horario_mensal 
                          ? 'Baseado no seu horário mensal agendado. Apenas confirme abaixo.' 
                          : 'Baseado no seu horário configurado. Se não estiver correto, altere abaixo.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Mostrar todos os turnos do dia quando há múltiplos */}
              {turnosDoDia.length > 1 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                  <p className="text-xs text-purple-600 font-medium mb-2">📅 Seus turnos hoje ({turnosDoDia.length} turnos):</p>
                  <div className="space-y-1">
                    {turnosDoDia.map((turno, idx) => (
                      <div 
                        key={idx} 
                        className={`text-sm px-2 py-1 rounded ${
                          lojaSelecionada === turno.local && intervaloSelecionado?.hora === (tipoRegistoModal === 'entrada' ? turno.entrada : turno.saida)
                            ? 'bg-purple-200 font-semibold'
                            : 'bg-white'
                        }`}
                      >
                        <span className="font-medium">{turno.local}:</span> {turno.entrada} - {turno.saida}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Se for turno agendado, mostrar informações de confirmação */}
              {intervaloSelecionado?.from_horario_mensal ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-xs text-blue-600 font-medium mb-1">Local/Seção</p>
                    <p className="text-lg font-semibold text-blue-900">{lojaSelecionada}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-600 font-medium mb-1">
                      {tipoRegistoModal === 'entrada' ? 'Hora de Entrada Agendada' : 'Hora de Saída Agendada'}
                    </p>
                    <p className="text-2xl font-bold text-blue-900">{intervaloSelecionado.hora}</p>
                  </div>
                </div>
              ) : (
                /* Seleção manual quando não há turno agendado */
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">Loja</label>
                    <select
                      value={lojaSelecionada}
                      onChange={(e) => {
                        setLojaSelecionada(e.target.value);
                        setTurnoSelecionado('');
                        setIntervaloSelecionado(null);
                      }}
                      className="w-full px-3 py-2 border rounded-md bg-background"
                      required
                    >
                      <option value="">Selecione a loja</option>
                      <option value="Café">Café</option>
                      <option value="Loja Avelal">Loja Avelal</option>
                      <option value="Loja Sátão">Loja Sátão</option>
                      <option value="Apoio">Apoio</option>
                      <option value="Gestão">Gestão</option>
                    </select>
                  </div>

                  {lojaSelecionada && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Turno</label>
                      <select
                        value={turnoSelecionado}
                        onChange={(e) => {
                          setTurnoSelecionado(e.target.value);
                          setIntervaloSelecionado(null);
                        }}
                        className="w-full px-3 py-2 border rounded-md bg-background"
                        required
                      >
                        <option value="">Selecione o turno</option>
                        <option value="manha">Manhã</option>
                        <option value="tarde">Tarde</option>
                      </select>
                    </div>
                  )}

                  {turnoSelecionado && (
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        {tipoRegistoModal === 'entrada' ? 'Hora de Entrada' : 'Hora de Saída'}
                      </label>
                      <div className="space-y-2">
                        {getIntervalosDisponiveis().map((item, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setIntervaloSelecionado(item)}
                            className={`w-full px-4 py-3 border rounded-md text-left transition-colors ${
                              intervaloSelecionado?.hora === item.hora
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background hover:bg-accent border-border'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-lg">{item.hora}</span>
                              {intervaloSelecionado?.hora === item.hora && (
                                <span className="text-sm">✓</span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowRegistoModal(false);
                    setError(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={confirmarRegistoPonto}
                  className={tipoRegistoModal === 'entrada' ? 'bg-success hover:bg-success/90' : 'bg-warning hover:bg-warning/90'}
                  disabled={!intervaloSelecionado}
                >
                  Confirmar {tipoRegistoModal === 'entrada' ? 'Entrada' : 'Saída'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 no-print">
          <div className="bg-card rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {editingRegisto ? 'Editar' : 'Novo'} Registo de Ponto
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Data</label>
                <input
                  type="date"
                  value={formData.data}
                  onChange={(e) => updateField('data', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tipo</label>
                <select
                  value={formData.tipo}
                  onChange={(e) => updateField('tipo', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  required
                >
                  <option value="entrada">Entrada</option>
                  <option value="saida">Saída</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Hora</label>
                <input
                  type="time"
                  value={formData.hora}
                  onChange={(e) => updateField('hora', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  required
                  disabled={!editingRegisto}
                  title={editingRegisto ? "Editável apenas para correções" : "Hora bloqueada no momento do registo"}
                />
                {!editingRegisto && (
                  <p className="text-xs text-muted-foreground mt-1">
                    ⚠️ Use os botões &quot;Registar Entrada/Saída&quot; para registar com hora atual
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Observações (opcional)</label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => updateField('observacoes', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  rows={3}
                  placeholder="Notas adicionais..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90">
                  {editingRegisto ? 'Atualizar' : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Seção de Assinatura (apenas impressão) */}
      <div className="print-only print-signatures">
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '2cm', paddingRight: '2cm' }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ marginBottom: '0.8cm', fontSize: '8pt', color: '#666' }}>Data: _____ / _____ / _________</p>
            <div style={{ borderTop: '1px solid #000', paddingTop: '0.1cm', width: '6cm', margin: '0 auto' }}>
              <p style={{ fontWeight: '600', fontSize: '9pt' }}>Assinatura do Colaborador</p>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ marginBottom: '0.8cm', fontSize: '8pt', color: '#666' }}>Data: _____ / _____ / _________</p>
            <div style={{ borderTop: '1px solid #000', paddingTop: '0.1cm', width: '6cm', margin: '0 auto' }}>
              <p style={{ fontWeight: '600', fontSize: '9pt' }}>Assinatura do Responsável</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistoPontoTab;
